import { FormProvider, useForm } from 'react-hook-form'
import { SettingFinanceFilter } from './SettingFinanceFilter'
import { Box, Checkbox, Divider, IconButton, InputAdornment, Menu, MenuItem } from '@mui/material'
import { CompanyLogo } from '@/components/common/CompanyLogo'
import { Button } from '@/@core/components/mui/Button'
import { TablePagination, TablePaginationProps } from '@/@core/components/mui/TablePagination'
import { useNotification, useRouteQuery } from '@/hooks'
import { useNavigate } from 'react-router-dom'
import FormInput from '@/components/form/FormInput'
import { useUpdateBalanceSettingFinanceBulkMutation } from '@/@data/balance'
import { IHttpError } from '@/@data'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import dayjs from 'dayjs'

export const SettingFinanceTable = ({ data, meta, onSuccess }: { data: any; meta: any; onSuccess: () => void }) => {
  const { __, __n } = useTranslator()
  const { searchQuery } = useRouteQuery()
  const navigate = useNavigate()
  const n = useNotification()

  const [isAllChecked, setIsAllChecked] = useState<boolean>(false)
  const [excludedCompanies, setExcludedCompanies] = useState<Set<number>>(new Set())
  const [selectedCompanies, setSelectedCompanies] = useState<Set<number>>(new Set())
  const [selectionAnchorEl, setSelectionAnchorEl] = useState<null | HTMLElement>(null)
  const selectionOpen = Boolean(selectionAnchorEl)

  const colorsAction = {
    active: 'success',
    disable: 'secondary',
    terminated: 'secondary',
    terminated_pending: 'warning',
    expired: 'error'
  }

  const getSelectedCount = () => {
    if (isAllChecked) {
      const totalCount = (meta as any)?.total || 0

      return totalCount - excludedCompanies.size
    } else {
      return selectedCompanies.size
    }
  }

  // Handle check all functionality
  const handleCheckAll = (checked: boolean) => {
    if (checked) {
      setIsAllChecked(true)
      setExcludedCompanies(new Set())
      setSelectedCompanies(new Set())
      methods.setValue('is_all', 1)
      methods.setValue('company_ids', [])
      methods.setValue('exclude_company_ids', [])
    } else {
      setIsAllChecked(false)
      setExcludedCompanies(new Set())
      setSelectedCompanies(new Set())
      methods.setValue('is_all', 0)
      methods.setValue('company_ids', [])
      methods.setValue('exclude_company_ids', [])
    }
  }

  // Handle individual checkbox
  const handleIndividualCheck = (id: number, checked: boolean) => {
    if (isAllChecked) {
      // flow 1: check all mode - manage excluded companies
      const newExcludedCompanies = new Set(excludedCompanies)

      if (!checked) {
        newExcludedCompanies.add(id)
      } else {
        newExcludedCompanies.delete(id)
      }

      setExcludedCompanies(newExcludedCompanies)

      const excludeArray = Array.from(newExcludedCompanies)

      methods.setValue('exclude_company_ids', excludeArray as never[])
    } else {
      // flow 2: individual selection mode - manage selected companies
      const newSelectedCompanies = new Set(selectedCompanies)

      if (checked) {
        newSelectedCompanies.add(id)
      } else {
        newSelectedCompanies.delete(id)
      }

      setSelectedCompanies(newSelectedCompanies)

      const idsArray = Array.from(newSelectedCompanies)

      methods.setValue('company_ids', idsArray as never[])
    }
  }

  const schema = yup.object().shape({
    vat_rate: yup
      .number()
      .transform((value, originalValue) => {
        return originalValue === '' || originalValue === null ? undefined : value
      })
      .typeError(__('VAT must be a number'))
      .min(0)
      .max(100)
      .notRequired(),
    service_fee: yup
      .number()
      .transform((value, originalValue) => {
        return originalValue === '' || originalValue === null ? undefined : value
      })
      .typeError(__('Service Fee must be a number'))
      .min(0)
      .notRequired()
  })

  const methods = useForm<any>({
    defaultValues: {
      vat_rate: 0,
      service_fee: 0,
      is_all: 0,
      company_ids: [],
      exclude_company_ids: []
    },
    mode: 'onChange',
    resolver: yupResolver(schema)
  })

  const { mutate: updateBalanceSettingFinanceBulk } = useUpdateBalanceSettingFinanceBulkMutation()

  const onSubmit = async (data: any) => {
    const payload = {
      ...data,
      vat_rate: Number(data.vat_rate),
      service_fee: Number(data.service_fee)
    }

    updateBalanceSettingFinanceBulk(payload, {
      onSuccess: () => {
        n.success(__('Update balance setting finance bulk successfully'))
        setSelectedCompanies(new Set())
        setExcludedCompanies(new Set())
        setIsAllChecked(false)
        methods.reset()
        onSuccess()
      },
      onError(error: IHttpError) {
        n.error(error.message)
      }
    })
  }

  return (
    <>
      <SettingFinanceFilter />
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          {getSelectedCount() > 0 && (
            <>
              <Divider />
              <div className='flex items-end justify-between px-5 pb-5 pt-8'>
                <div className='flex items-start gap-3'>
                  <div>
                    <Button
                      variant='outlined'
                      onClick={e => {
                        setSelectionAnchorEl(e.currentTarget)
                      }}
                      endIcon={<i className='tabler-chevron-down text-gray-600' />}
                      sx={{
                        borderRadius: '20px',
                        borderColor: '#d0d0d0',
                        color: '#666',
                        textTransform: 'none',
                        '&:hover': {
                          borderColor: '#b0b0b0'
                        }
                      }}
                    >
                      <span className='text-sm font-medium'>
                        {getSelectedCount()} {__('Selected')}
                      </span>
                    </Button>
                    <Menu
                      anchorEl={selectionAnchorEl}
                      open={selectionOpen}
                      onClose={() => {
                        setSelectionAnchorEl(null)
                      }}
                      anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left'
                      }}
                      transformOrigin={{
                        vertical: 'top',
                        horizontal: 'left'
                      }}
                      sx={{
                        borderRadius: 2,
                        mt: 1,
                        minWidth: 200
                      }}
                    >
                      <MenuItem
                        onClick={() => {
                          handleCheckAll(true)
                          setSelectionAnchorEl(null)
                        }}
                        sx={{ fontSize: '14px' }}
                      >
                        {(meta as any)?.total || 0} {__('Selected')}
                      </MenuItem>
                      <MenuItem
                        onClick={() => {
                          handleCheckAll(false)
                          setSelectionAnchorEl(null)
                        }}
                        sx={{ fontSize: '14px' }}
                      >
                        {__('Un-Selected')}
                      </MenuItem>
                    </Menu>
                  </div>
                  <div className='w-[250px] relative'>
                    <div className='text-[13px] font-medium text-[#2f2b3db3] mb-2 absolute top-[-18px] left-0'>
                      {__('Fee charge')}
                    </div>
                    <FormInput
                      name='service_fee'
                      placeholder={__('Enter Value')}
                      control={methods.control}
                      slotProps={{
                        input: {
                          endAdornment: <InputAdornment position='end'>&#8363;</InputAdornment>
                        }
                      }}
                      className='w-full'
                      onClear={() => {
                        methods.setValue('service_fee', undefined)
                      }}
                    />
                  </div>
                  <div className='w-[180px] relative'>
                    <div className='text-[13px] font-medium text-[#2f2b3db3] mb-2 absolute top-[-18px] left-0'>
                      {__('V.A.T')}
                    </div>
                    <FormInput
                      name='vat_rate'
                      placeholder={__('Enter Value')}
                      control={methods.control}
                      slotProps={{
                        input: {
                          endAdornment: (
                            <InputAdornment position='end'>
                              <i className='tabler-percentage text-[#2f2b3de6]' style={{ fontSize: 16 }} />
                            </InputAdornment>
                          ),
                          onInput: (e: any) => {
                            if (e.target.value < 0) {
                              e.target.value = 0
                            }

                            if (e.target.value > 100) {
                              e.target.value = 100
                            }
                          }
                        }
                      }}
                      className='w-full'
                      onClear={() => {
                        methods.setValue('vat_rate', undefined)
                      }}
                    />
                  </div>
                </div>
                <div>
                  <div className='flex items-center gap-2'>
                    <Button
                      variant='contained'
                      color='primary'
                      type='submit'
                      startIcon={<i className='tabler-check' />}
                    >
                      {__('Submit')}
                    </Button>
                    <Button
                      variant='tonal'
                      color='secondary'
                      startIcon={<i className='tabler-x' />}
                      onClick={() => {
                        setSelectedCompanies(new Set())
                        setExcludedCompanies(new Set())
                        setIsAllChecked(false)
                        methods.reset()
                      }}
                    >
                      {__('Clear')}
                    </Button>
                  </div>
                </div>
              </div>
            </>
          )}
          <div>
            <Box sx={{ overflowX: 'auto' }}>
              <table
                style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  border: '1px solid #e0e0e0'
                }}
              >
                <thead>
                  <tr className='border-right-custom'>
                    <th
                      style={{
                        padding: '12px 16px',
                        textAlign: 'left',

                        borderBottom: '1px solid #e0e0e0',
                        fontWeight: '700',
                        fontSize: '13px'
                      }}
                    >
                      <Checkbox
                        checked={isAllChecked}
                        indeterminate={!isAllChecked && selectedCompanies.size > 0}
                        onChange={e => handleCheckAll(e.target.checked)}
                      />
                    </th>
                    <th
                      style={{
                        padding: '12px 16px',
                        textAlign: 'left',
                        borderBottom: '1px solid #e0e0e0',
                        fontWeight: '700',
                        fontSize: '13px',
                        textTransform: 'uppercase'
                      }}
                    >
                      {__('Employer')}
                    </th>
                    <th
                      style={{
                        padding: '12px 16px',
                        textAlign: 'left',
                        borderBottom: '1px solid #e0e0e0',
                        fontWeight: '700',
                        fontSize: '13px',
                        textTransform: 'uppercase'
                      }}
                    >
                      {__('QTY Voucher')}
                    </th>
                    <th
                      style={{
                        padding: '12px 16px',
                        textAlign: 'left',
                        borderBottom: '1px solid #e0e0e0',
                        fontWeight: '700',
                        fontSize: '13px',
                        textTransform: 'uppercase'
                      }}
                    >
                      {__('Voucher Amount')}
                    </th>
                    <th
                      style={{
                        padding: '12px 16px',
                        textAlign: 'left',
                        borderBottom: '1px solid #e0e0e0',
                        fontWeight: '700',
                        fontSize: '13px',
                        textTransform: 'uppercase'
                      }}
                    >
                      {__('Fee charge')}
                    </th>
                    <th
                      style={{
                        padding: '12px 16px',
                        textAlign: 'left',
                        borderBottom: '1px solid #e0e0e0',
                        fontWeight: '700',
                        fontSize: '13px',
                        textTransform: 'uppercase'
                      }}
                    >
                      {__('v.a.t')}
                    </th>
                    <th
                      style={{
                        padding: '12px 16px',
                        textAlign: 'left',
                        borderBottom: '1px solid #e0e0e0',
                        fontWeight: '700',
                        fontSize: '13px',
                        textTransform: 'uppercase'
                      }}
                    >
                      {__('Range date apply')}
                    </th>
                    <th
                      style={{
                        padding: '12px 16px',
                        textAlign: 'left',
                        borderBottom: '1px solid #e0e0e0',
                        fontWeight: '700',
                        fontSize: '13px',
                        textTransform: 'uppercase'
                      }}
                    >
                      {__('Status')}
                    </th>
                    <th
                      style={{
                        padding: '12px 16px',
                        textAlign: 'left',
                        borderBottom: '1px solid #e0e0e0',
                        fontWeight: '700',
                        fontSize: '13px',
                        textTransform: 'uppercase'
                      }}
                    >
                      {__('Action')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((item: any, index: any) => (
                    <tr key={index}>
                      <td
                        style={{
                          padding: '12px 16px',
                          textAlign: 'left',
                          borderBottom: '1px solid #e0e0e0',
                          fontWeight: '500',
                          fontSize: '15px',
                          textTransform: 'uppercase'
                        }}
                      >
                        <Checkbox
                          checked={
                            isAllChecked
                              ? !excludedCompanies.has(Number(item?.id))
                              : selectedCompanies.has(Number(item?.id))
                          }
                          onChange={e => handleIndividualCheck(Number(item?.id), e.target.checked)}
                        />
                      </td>
                      <td
                        style={{
                          padding: '12px 16px',
                          textAlign: 'left',
                          borderBottom: '1px solid #e0e0e0',
                          fontWeight: '500',
                          fontSize: '15px'
                        }}
                      >
                        <CompanyLogo company={item} link={`/cms/finance/balance/setting-finance/${item?.id}`} />
                      </td>
                      <td
                        style={{
                          padding: '12px 16px',
                          textAlign: 'left',
                          borderBottom: '1px solid #e0e0e0',
                          fontWeight: '500',
                          fontSize: '15px'
                        }}
                      >
                        0
                      </td>
                      <td
                        style={{
                          padding: '12px 16px',
                          textAlign: 'left',
                          borderBottom: '1px solid #e0e0e0',
                          fontWeight: '500',
                          fontSize: '15px'
                        }}
                      >
                        0
                      </td>
                      <td
                        style={{
                          padding: '12px 16px',
                          textAlign: 'left',
                          borderBottom: '1px solid #e0e0e0',
                          fontWeight: '500',
                          fontSize: '15px'
                        }}
                      >
                        {__n(item?.service_fee, 'đ')}
                      </td>
                      <td
                        style={{
                          padding: '12px 16px',
                          textAlign: 'left',
                          borderBottom: '1px solid #e0e0e0',
                          fontWeight: '500',
                          fontSize: '15px'
                        }}
                      >
                        {__n(item?.vat_rate)} %
                      </td>
                      <td
                        style={{
                          padding: '12px 16px',
                          textAlign: 'left',
                          borderBottom: '1px solid #e0e0e0',
                          fontWeight: '400',
                          fontSize: '15px',
                          width: '250px'
                        }}
                      >
                        {item?.active_contract?.start_date ? dayjs(item?.active_contract?.start_date).format('DD/MM/YYYY') : ''} -{' '}
                        {item?.active_contract?.end_date ? dayjs(item?.active_contract?.end_date).format('DD/MM/YYYY') : ''}
                      </td>
                      <td
                        style={{
                          padding: '12px 16px',
                          textAlign: 'left',
                          borderBottom: '1px solid #e0e0e0',
                          fontWeight: '500',
                          fontSize: '15px'
                        }}
                      >
                        {item?.active_contract?.status ? (
                        <Button
                          variant='tonal'
                          color={
                            colorsAction[item?.active_contract?.status as keyof typeof colorsAction] as
                              | 'success'
                              | 'secondary'
                              | 'error'
                              | 'inherit'
                              | 'primary'
                              | 'info'
                              | 'warning'
                              | undefined
                          }
                          size='small'
                          className='capitalize'
                        >
                          <span className='text-[13px] font-medium'>{item?.active_contract?.status || '-'}</span>
                        </Button>
                        ) :
                        <span className='flex items-center justify-center'>-</span>
                        }
                      </td>
                      <td
                        style={{
                          padding: '12px 16px',
                          textAlign: 'left',
                          borderBottom: '1px solid #e0e0e0',
                          fontWeight: '500',
                          fontSize: '15px'
                        }}
                      >
                        <IconButton
                          size='small'
                          onClick={() => {
                            navigate(`/cms/finance/balance/setting-finance/${item?.id}`)
                          }}
                        >
                          <i className='tabler-edit' />
                        </IconButton>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
            {meta && (
              <TablePagination
                meta={
                  {
                    ...meta,
                    page: Number(searchQuery.get('page')) || 1
                  } as unknown as TablePaginationProps
                }
              />
            )}
          </div>
        </form>
      </FormProvider>
    </>
  )
}
